<template>
  <header class="header header--mobile header--internal header--ios">
    <div class="header__inner">
      <BackLink class="header__backlink" />
      <div class="header__title-wrapper">
        <h1 v-if="title" class="header__title">
          {{ title }}
        </h1>
      </div>
      <div class="header__actions">
        <slot name="actions" />
        <StartFreeTrialButton v-if="isFreePlan" />
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { DocView } from '@/models/doc'
import { auth } from '@/services/auth'

import BackLink from '@/app/components/BackLink.vue'
import StartFreeTrialButton from '@/app/components/header/StartFreeTrialButton.vue'

defineProps<{
  title: string
  backLinkDoc?: DocView
}>()

const isFreePlan = computed(() => auth.isFreePlan())
</script>

<style lang="scss" scoped>
/**
 * Extends `.header` and `.header--mobile` classes, which are defined in `@/assets.app/css/components/_header.scss`
 */
.header {
  transition: none;

  &--internal {
    transition: all 0.2s;
  }

  &__inner {
    display: grid;
    grid-template-columns: auto 1fr auto;
    column-gap: 30px;
    row-gap: 0;
    transition: none;
    @media only screen and (max-width: $w-small) {
      gap: 15px;
    }
  }

  &__title-wrapper {
    grid-column: span 2;
    overflow: hidden;
  }

  &__title {
    transition: none;
    // To compensate for Rotunda's line-height,
    // allowing us to center the title visually
    margin-top: 5px;
  }

  &__backlink {
    display: inline-flex;
    grid-column: span 3;
    margin-bottom: 16px;
    transition: all 0.2s ease-in-out;
    @media only screen and (max-width: $w-medium-s) {
      margin-block: 8px;
    }

    :deep(.back-link__text) {
      font-size: 16px;
      letter-spacing: -0.02em;
    }

    :deep(.back-link__icon) {
      font-size: 20px;
      margin-bottom: -1px;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 15px;
    width: 100%;
  }

  &.header--scroll {
    // This curve is a longer ease-in-out. We use it
    // here because the start of the tansition of
    // .header__back's margin is too abrupt, and the longer
    // ease-in helps smooth it out.
    $longerEaseInOut: cubic-bezier(0.65, 0, 0.35, 1);
    padding-top: 40px;
    padding-bottom: 20px;
    @media only screen and (max-width: $w-medium-s) {
      padding-top: 20px;
    }

    .header {
      &__inner {
        grid-template-columns: 1fr minmax(auto, 50%) 1fr;
      }

      &__backlink {
        grid-column: 1;
        margin-bottom: 0;
        animation: fadeIn 0.6s $longerEaseInOut;
      }

      &__title-wrapper {
        grid-column: 2;
        justify-content: center;
        animation: fadeIn 0.6s $longerEaseInOut;
      }

      &__title {
        font-size: 18px;
        line-height: 30px;
        @media only screen and (max-width: $w-medium-s) {
          line-height: 28px;
        }
      }

      &__actions {
        @media only screen and (max-width: $w-small) {
          gap: 5px;
        }
      }
    }
  }
}
</style>
